import React, { useState } from 'react';
import { useProjectDetailsContext } from '../contexts/ProjectDetailsContext';
import { useParams } from 'react-router-dom';
import ApprovalCheckBox from './ApprovalCheckBox';
import StatusIndicator from './StatusIndicator';
import { formatDate } from '../utils/timeUtils';
import { ApprovalStatus, Stage } from '../types';
import { useUser } from '@clerk/clerk-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faX } from '@fortawesome/free-solid-svg-icons';
import ReviewConfirmationModal from './ReviewConfirmationModal';
import ApprovalStatusModal from './ApprovalStatusModal';
import { useLabelContext } from '../contexts/LabelContext';

interface ApprovalStageProps {
    title: string;
    status?: ApprovalStatus;
    explanation?: string;
    comment?: string;
    isPendingReviewer?: boolean;
    onApprove?: () => void;
    onReject?: () => void;
    renderStatus?: boolean;
    onStatusEdit?: () => void;
}

// Extracted reusable Stage component
const ApprovalStage: React.FC<ApprovalStageProps> = ({
    title,
    status,
    explanation,
    comment,
    isPendingReviewer,
    onApprove,
    onReject,
    renderStatus = false,
    onStatusEdit,
}) => (
    <div className="approval-stage">
        <ApprovalCheckBox status={status} invert={false} />
        <div className="info">
            <div className="top">
                <h4>{title}</h4>
                {status && renderStatus && (
                    <StatusIndicator
                        decision={status}
                        isBig={false}
                        onEdit={onStatusEdit}
                    />
                )}
            </div>
            {explanation && <p className="explanation">{explanation}</p>}
            {comment && (
                <div className="comment-container">
                    <p className="comment">{comment}</p>
                </div>
            )}
            {isPendingReviewer && (
                <div className="approval-actions">
                    <button className="approve-button" onClick={onApprove}>
                        <FontAwesomeIcon icon={faCheck} /> Approve
                    </button>
                    <button className="reject-button" onClick={onReject}>
                        <FontAwesomeIcon icon={faX} /> Reject
                    </button>
                </div>
            )}
        </div>
    </div>
);

// Divider component for cleaner JSX
const Divider: React.FC = () => <hr className="divider" />;

const LabelApprovalDetails: React.FC = () => {
    const {
        state: { project, userDetailsMapping },
    } = useProjectDetailsContext();
    const {
        state: { labelDetailed },
    } = useLabelContext();
    const approvalStages = project?.workflow.stages;
    const { user } = useUser();
    const [statusToConfirm, setStatusToConfirm] = useState<
        ApprovalStatus.APPROVED | ApprovalStatus.REJECTED
    >();
    const [stageToConfirm, setStageToConfirm] = useState<Stage>();
    const [editApprovalStatus, setEditApprovalStatus] = useState(false);

    if (!labelDetailed || !approvalStages || approvalStages.length === 0) {
        return null;
    }

    // Handle review action (approve/reject)
    const handleReviewAction = (
        stage: Stage,
        status: ApprovalStatus.APPROVED | ApprovalStatus.REJECTED,
    ) => {
        setStatusToConfirm(status);
        setStageToConfirm(stage);
    };

    // Render AI Review stage
    const renderAIReview = () => {
        const statusEditable = !labelDetailed.reviewed_stages.some(
            (reviewedStage) =>
                reviewedStage.status === ApprovalStatus.APPROVED ||
                reviewedStage.status === ApprovalStatus.REJECTED,
        );
        const onStatusEdit = statusEditable
            ? () => {
                  setEditApprovalStatus(true);
              }
            : undefined;
        return (
            <ApprovalStage
                title="AI Review"
                status={labelDetailed.approval_status}
                explanation={formatDate(
                    labelDetailed.creation.timestamp,
                    true,
                    true,
                )}
                renderStatus={true}
                onStatusEdit={onStatusEdit}
            />
        );
    };

    // Render workflow stages
    const renderWorkflowStages = () =>
        approvalStages.map((stage) => {
            const reviewedStage = labelDetailed.reviewed_stages.find(
                (reviewedStage) => reviewedStage.id === stage.id,
            );

            // If stage hasn't been reviewed
            if (!reviewedStage) {
                return (
                    <React.Fragment key={stage.id}>
                        <Divider />
                        <ApprovalStage title={stage.name} />
                    </React.Fragment>
                );
            }

            // Get reviewer details
            const reviewerDetails = userDetailsMapping[reviewedStage.user_id];
            const reviewerName = reviewerDetails
                ? `${reviewerDetails.first_name} ${reviewerDetails.last_name}`
                : 'Unknown Reviewer';

            // Pending stage
            if (reviewedStage.status === 'Pending') {
                // Check if current user is the assigned reviewer
                const isCurrentUserReviewer =
                    user?.id === reviewedStage.user_id;

                return (
                    <React.Fragment key={stage.id}>
                        <Divider />
                        <ApprovalStage
                            title={stage.name}
                            explanation={`Awaiting Review by ${reviewerName}`}
                            isPendingReviewer={isCurrentUserReviewer}
                            onApprove={() =>
                                handleReviewAction(
                                    stage,
                                    ApprovalStatus.APPROVED,
                                )
                            }
                            onReject={() =>
                                handleReviewAction(
                                    stage,
                                    ApprovalStatus.REJECTED,
                                )
                            }
                        />
                    </React.Fragment>
                );
            }

            // Completed stage
            return (
                <React.Fragment key={stage.id}>
                    <Divider />
                    <ApprovalStage
                        title={stage.name}
                        status={reviewedStage.status}
                        explanation={`${reviewedStage.status} by ${reviewerName} (${formatDate(
                            reviewedStage.timestamp,
                            true,
                            true,
                        )})`}
                        comment={reviewedStage.comment ?? undefined}
                    />
                </React.Fragment>
            );
        });

    return (
        <>
            <ReviewConfirmationModal
                onClose={() => {
                    setStatusToConfirm(undefined);
                    setStageToConfirm(undefined);
                }}
                status={statusToConfirm}
                stage={stageToConfirm}
            />
            <ApprovalStatusModal
                isActive={editApprovalStatus}
                onClose={() => setEditApprovalStatus(false)}
            />

            <div className="label-approval-details">
                {renderAIReview()}
                {renderWorkflowStages()}
            </div>
        </>
    );
};

export default LabelApprovalDetails;
